import {
	Authenticator,
	getAuthenticator,
	HttpClient,
} from '@sisense/sdk-rest-client';
import { normalizeUrl } from './utils';
import { getTenantPrefix } from './proxy-utils';

export const HEADER_SISENSE_EMBED = 'x-sisense-embed';

/** Value for {@link HEADER_SISENSE_EMBED} when the app runs inside an iframe. */
export const SISENSE_EMBED_IFRAME = 'iframe';

export const DEFAULT_HEADERS = {
	'x-sisense-ai': 'true',
	'x-sisense-origin': 'fusion-ui',
};

/**
 * True when running in a browser inside an iframe (including cross-origin embeds
 * where comparing to `top` may throw — treated as embedded).
 */
export function isEmbeddedInIframe(): boolean {
	if (typeof window === 'undefined') {
		return false;
	}
	try {
		return window.self !== window.top;
	} catch {
		return true;
	}
}

/** Default telemetry headers for AI SDK HTTP and completion requests. */
export function getDefaultHttpHeaders(): Record<string, string> {
	return {
		...DEFAULT_HEADERS,
		...(isEmbeddedInIframe()
			? { [HEADER_SISENSE_EMBED]: SISENSE_EMBED_IFRAME }
			: {}),
	};
}

// Re-export from CSDK for convenience
export type AuthenticatorConfig = {
	url: string;
	username?: string;
	password?: string;
	token?: string | null;
	wat?: string | null;
	ssoEnabled?: boolean;
	enableSilentPreAuth?: boolean;
	useFusionAuth?: boolean;
	alternativeSsoHost?: string;
};

/**
 * Factory function to create an HTTP client from an authenticator
 * @param url - The URL to use for the HTTP client
 * @param authenticator - The authenticator to use for the HTTP client
 * @returns Configured HttpClient instance
 */
export function createHttpClientFromAuthenticator(
	url: string,
	authenticator: Authenticator,
	headers: Record<string, string> = getDefaultHttpHeaders(),
): HttpClient {
	let tenantPrefix = getTenantPrefix();
	if (tenantPrefix.startsWith('/')) {
		tenantPrefix = tenantPrefix.slice(1);
	}
	const httpClientInstance = new HttpClient(
		`${normalizeUrl(url)}${tenantPrefix}`,
		authenticator,
		'sdk-ai-core' + (__PACKAGE_VERSION__ ? `-${__PACKAGE_VERSION__}` : ''),
		headers,
	);
	httpClientInstance.login();
	return httpClientInstance;
}

/**
 * Factory function to create an HTTP client from an authenticator configuration
 * @param config - Authenticator configuration
 * @returns Configured HttpClient instance
 */
export function createHttpClientFromConfig(
	config: AuthenticatorConfig,
	headers: Record<string, string> = getDefaultHttpHeaders(),
): HttpClient {
	const authenticator = getAuthenticator(config);

	if (!authenticator) {
		throw new Error('Invalid authentication method');
	}

	return createHttpClientFromAuthenticator(
		config.url,
		authenticator,
		headers,
	);
}

/**
 * Extracts HTTP status code from various error shapes (Axios, fetch, custom).
 */
export function getHttpErrorStatus(error: unknown): number | undefined {
	const err = error as Record<string, unknown>;
	const raw =
		err?.status ??
		err?.statusCode ??
		(err?.response as Record<string, unknown>)?.status;
	return raw !== undefined ? Number(raw) : undefined;
}
